import React from 'react';
import classNames from 'classnames';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import { Draggable } from 'react-beautiful-dnd';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import LinkOff from '@material-ui/icons/LinkOff';
import { getBorder, getBorderRadius } from 'Components/utils';
import { FDIcon, FDStack } from '@facet-wealth/design';

const useStyles = buildUseStyles('DraggableStepSet', ((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    padding: '4px 16px',
    margin: theme.spacing(1, 0),
  },
  rootIncompatible: {
    backgroundColor: 'rgba(245, 245, 245, 0.5)',
  },
  rootCompatible: {
    backgroundColor: 'rgba(0, 112, 152, 0.05)',
    ...getBorder(theme, {
      color: theme.palette.primary.main,
      all: true,
    }),
  },
  isGroup: {
    ...getBorder(theme, {
      color: theme.palette.primary.main,
      all: true,
      borderStyle: 'dashed',
    }),
    padding: theme.spacing(1, 2),
  },
  iconButton: {
    position: 'absolute',
    right: -20,
    top: -20,
    zIndex: 1,
  },
  iconButtonIcon: {
    background: '#007197',
    color: theme.palette.common.white,
    fontSize: '.75em',
    padding: '3px',
    ...getBorderRadius(theme.radius.full, { all: true }),
  },
  iconButtonHidden: {
    display: 'none',
  },
  dragHandleHidden: {
    display: 'none',
  },
})));

const DraggableStepSet = ({
  stepResources,
  isNeverDraggable,
  isCurrentlyNotDraggable,
  dragIdx,
  onBreakGroup,
  isDraggingOver,
  isCompatibleTarget,
  StepComponent,
}) => {
  const classes = useStyles();

  let active = true;
  for (const step of stepResources) {
    if (step.attr('isActive') === 'false') {
      active = false;
    }
  }

  return (
    <Draggable
      draggableId={`step-set.${stepResources[0].attr('stepSet')}`}
      index={dragIdx}
      isDragDisabled={isNeverDraggable || isCurrentlyNotDraggable}
    >
      {(dragProvided) => (
        <Paper
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          {...dragProvided.dragHandleProps}
          className={classNames(classes.root, {
            [classes.isDragging]: isDraggingOver,
            [classes.rootIncompatible]: isDraggingOver && isCompatibleTarget === false,
            [classes.rootCompatible]: isDraggingOver && isCompatibleTarget === true,
            [classes.isGroup]: stepResources.length > 1,
          })}
          style={{
            ...dragProvided.draggableProps.style,
          }}
        >
          <div className={classNames(classes.iconButton, {
            [classes.iconButtonHidden]: stepResources.length === 1 || isNeverDraggable,
          })}
          >
            <IconButton
              classes={{
                icon: classes.iconButtonIcon,
              }}
              icon={LinkOff}
              onClick={() => {
                return onBreakGroup(stepResources[0].attr('stepSet'));
              }}
              title={'Break Group'}
            />
          </div>
          <FDStack direction={'row'} alignItems={'center'} spacing={1} flexGrow={1}>
            <div
              className={classNames({}, {
                [classes.dragHandleHidden]: isNeverDraggable,
              })}
            >
              <FDIcon
                icon={'dragIndicator'}
                size={'md'}
                color={active ? 'textPrimary' : 'textDisabled'}
              />
            </div>
            <FDStack direction={'column'} flexGrow={1} spacing={2}>
              {stepResources.map((stepResource, idx) => {
                return (
                  <StepComponent
                    key={stepResource.id()}
                    index={idx}
                    stepResource={stepResource}
                  />
                );
              })}
            </FDStack>
          </FDStack>
        </Paper>
      )}
    </Draggable>
  );
};

DraggableStepSet.defaultProps = {
  isDraggingOver: false,
  isCompatibleDropTarget: null,
};

DraggableStepSet.propTypes = {
  stepResources: PropTypes.array.isRequired,
  dragIdx: PropTypes.number.isRequired,
  isNeverDraggable: PropTypes.bool.isRequired,
  isCurrentlyNotDraggable: PropTypes.bool.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  onBreakGroup: PropTypes.func.isRequired,
  isDraggingOver: PropTypes.bool,
  isCompatibleDropTarget: PropTypes.bool,
  StepComponent: PropTypes.func.isRequired,
};

export default DraggableStepSet;
